if (!customElements.get('product-sticky-cart')) {
  customElements.define(
    'product-sticky-cart',
    class ProductStickyCart extends HTMLElement {
      variants = [];
      selectedVariant = null;
      optionSelects = [];
      productInfo = null;
      trigger = null;
      variantChangeUnsubscriber = null;
      scrollFrame = null;
      footerObserver = null;
      footerVisible = false;

      connectedCallback() {
        this.variants = this.parseVariants();
        this.optionSelects = Array.from(this.querySelectorAll('[data-sticky-cart-option]'));
        this.productInfo = this.closest('product-info');
        this.trigger =
          this.productInfo?.querySelector('[id^="ProductSubmitButton-"]') ||
          this.productInfo?.querySelector('.product-variant-cards') ||
          this.productInfo?.querySelector('.product__info-container');

        this.optionSelects.forEach((select) => select.addEventListener('change', this.handleOptionChange));
        window.addEventListener('scroll', this.handleViewportChange, { passive: true });
        window.addEventListener('resize', this.handleViewportChange, { passive: true });
        this.observeFooter();

        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data.sectionId !== this.dataset.sectionId || !data.variant?.id) return;
            this.updateFromVariantId(data.variant.id);
            this.removeAttribute('aria-busy');
          });
        }

        this.updateFromVariantId(this.dataset.selectedVariantId);
        this.updateVisibility();
      }

      disconnectedCallback() {
        this.optionSelects.forEach((select) => select.removeEventListener('change', this.handleOptionChange));
        window.removeEventListener('scroll', this.handleViewportChange);
        window.removeEventListener('resize', this.handleViewportChange);
        this.variantChangeUnsubscriber?.();
        this.footerObserver?.disconnect();
        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      }

      parseVariants() {
        try {
          return JSON.parse(this.querySelector('[data-sticky-cart-variants]')?.textContent || '[]');
        } catch (error) {
          console.error('Unable to parse sticky cart variants.', error);
          return [];
        }
      }

      handleOptionChange = () => {
        const selectedOptions = this.optionSelects.map((select) => select.value);
        const variant = this.variants.find(
          (candidate) =>
            candidate.options.length === selectedOptions.length &&
            candidate.options.every((option, index) => option === selectedOptions[index])
        );

        if (!variant) {
          this.setUnavailable();
          return;
        }

        this.updateVariant(variant);
        this.setAttribute('aria-busy', 'true');

        const fallbackUrl = `${this.dataset.productUrl}?variant=${encodeURIComponent(variant.id)}`;
        if (typeof this.productInfo?.selectVariantById === 'function') {
          this.productInfo.selectVariantById(variant.id, this.id, fallbackUrl);
        } else {
          window.location.assign(fallbackUrl);
        }
      };

      handleViewportChange = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateVisibility();
        });
      };

      observeFooter() {
        const footer =
          document.querySelector('.shopify-section-group-footer-group') ||
          document.querySelector('footer.footer') ||
          document.querySelector('footer');

        if (!footer || !('IntersectionObserver' in window)) return;

        this.footerObserver = new IntersectionObserver(([entry]) => {
          this.footerVisible = entry.isIntersecting;
          this.updateVisibility();
        });
        this.footerObserver.observe(footer);
      }

      updateVisibility() {
        if (!this.trigger) return;
        const hasPassedPurchaseArea = this.trigger.getBoundingClientRect().bottom < 0;
        const shouldShow = hasPassedPurchaseArea && !this.footerVisible;
        this.classList.toggle('is-visible', shouldShow);
        this.setAttribute('aria-hidden', String(!shouldShow));
      }

      updateFromVariantId(variantId) {
        const variant = this.variants.find((candidate) => String(candidate.id) === String(variantId));
        if (!variant) return;
        this.updateVariant(variant);
      }

      updateVariant(variant) {
        this.selectedVariant = variant;
        this.dataset.selectedVariantId = variant.id;

        this.optionSelects.forEach((select, index) => {
          if (variant.options[index] !== undefined) select.value = variant.options[index];
        });

        const variantInput = this.querySelector('[data-sticky-cart-variant-input]');
        if (variantInput) variantInput.value = variant.id;

        const price = this.querySelector('[data-sticky-cart-price]');
        if (price) price.textContent = variant.price;

        const comparePrice = this.querySelector('[data-sticky-cart-compare-price]');
        if (comparePrice) {
          comparePrice.textContent = variant.compareAtPrice || '';
          comparePrice.hidden = !variant.compareAtPrice;
        }

        const image = this.querySelector('[data-sticky-cart-image]');
        if (image && variant.image) {
          image.src = variant.image.src;
          image.alt = variant.image.alt || '';
        }

        this.updateQuantityRule(variant.quantityRule);
        this.updateSubmitButton(variant.available);
      }

      updateQuantityRule(quantityRule = {}) {
        const quantity = this.querySelector('[data-sticky-cart-quantity]');
        if (!quantity) return;

        const minimum = Number(quantityRule.min) || 1;
        const increment = Number(quantityRule.increment) || 1;
        quantity.min = minimum;
        quantity.step = increment;

        if (quantityRule.max === null || quantityRule.max === undefined) {
          quantity.removeAttribute('max');
        } else {
          quantity.max = quantityRule.max;
        }

        if (Number(quantity.value) < minimum) quantity.value = minimum;
      }

      updateSubmitButton(available) {
        const button = this.querySelector('[type="submit"]');
        const buttonText = this.querySelector('[data-sticky-cart-button-text]');
        if (!button || !buttonText) return;

        button.disabled = !available;
        button.toggleAttribute('aria-disabled', !available);
        buttonText.textContent = available ? this.dataset.addToCartLabel : this.dataset.soldOutLabel;
      }

      setUnavailable() {
        const variantInput = this.querySelector('[data-sticky-cart-variant-input]');
        if (variantInput) variantInput.value = '';
        this.updateSubmitButton(false);
      }
    }
  );
}
